import { useState } from "react";
import { Box, Collapse, IconButton, List, ListItem, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import { BulkCreateResult, RestaurantCreationFailure } from "src/data/admin/types/restaurant";
import {
    colorBorderSuccess,
    colorSurfaceSuccess,
    colorTextSuccess,
    colorTextError,
    colorTextMuted,
    shadowCard,
    radiusCard,
    iconColorSuccess,
    colorBorderError,
    colorSurfaceError,
} from "src/design/tokens";

interface BubbleProps {
    result: BulkCreateResult;
    onDismiss: () => void;
}

const FailureList = ({ failures }: { failures: RestaurantCreationFailure[] }) => (
    <List dense disablePadding sx={{ mt: 1 }}>
        {failures.map((f, i) => (
            <ListItem key={i} disablePadding sx={{ flexDirection: "column", alignItems: "flex-start", mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: colorTextError }}>
                    {f.restaurantName}
                </Typography>
                {f.errors.map((err, j) => (
                    <Typography key={j} variant="caption" sx={{ color: colorTextMuted }}>• {err}</Typography>
                ))}
            </ListItem>
        ))}
    </List>
);

const BulkRestaurantResultBubble = ({ result, onDismiss }: BubbleProps) => {
    const [expanded, setExpanded] = useState(false);
    const hasFailures = result.failures.length > 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
            {result.created > 0 && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        p: 2,
                        borderRadius: radiusCard,
                        border: `1px solid ${colorBorderSuccess}`,
                        backgroundColor: colorSurfaceSuccess,
                        boxShadow: shadowCard,
                    }}
                >
                    <CheckCircleOutlineIcon sx={{ color: iconColorSuccess, mt: 0.2, flexShrink: 0 }} />
                    <Box flex={1}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colorTextSuccess }}>
                            {result.created} restaurant{result.created !== 1 ? "s" : ""} created successfully
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={onDismiss} sx={{ mt: -0.5 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            {hasFailures && (
                <Box
                    sx={{
                        borderRadius: radiusCard,
                        border: `1px solid ${colorBorderError}`,
                        backgroundColor: colorSurfaceError,
                        boxShadow: shadowCard,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 2,
                            cursor: "pointer",
                        }}
                        onClick={() => setExpanded((v) => !v)}
                    >
                        <ErrorOutlineIcon sx={{ color: colorTextError, flexShrink: 0 }} />
                        <Box flex={1}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colorTextError }}>
                                {result.failed} restaurant{result.failed !== 1 ? "s" : ""} failed to create
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
                            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse in={expanded}>
                        <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${colorBorderError}` }}>
                            <FailureList failures={result.failures} />
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

export default BulkRestaurantResultBubble;
